/** The rail's Rooms tab: the sections, a room's menu, the open room with its places, and the All rooms list. */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import type { HostRoomRow, TalkRoomKind } from "../../shared/talk";
import { Icon } from "../components/ui/Icon";
import { useToast } from "../components/ui/Toast";
import { api } from "../lib/api";
import { Poster } from "./parts";
import { announcementsPath, circlePath, everyonePath, filesPath, type Place, questionsPath, roomHome } from "./places";
import { allRoomsView, kindLine, lastTalkWords, roomLive, roomNew } from "./rooms";

/** Keeps a room at the top for this team member, or takes it off, and says so. True when it was saved. */
export function useKeepRoom(onDone: () => void): (r: Pick<HostRoomRow, "webinarId" | "kept">) => Promise<boolean> {
  const toast = useToast();
  return useCallback(
    async (r) => {
      try {
        await api.talk.keep(r.webinarId, !r.kept);
        toast({ text: r.kept ? "Taken off the top" : "Kept at the top of your rooms", icon: "pin" });
        onDone();
        return true;
      } catch {
        toast({ text: "We couldn't save that. Try again.", icon: "info" });
        return false;
      }
    },
    [toast, onDone],
  );
}

/** A room's menu: keep it at the top, or open its page. */
function RoomMenu({ room, open, onToggle, onKeep }: { room: HostRoomRow; open: boolean; onToggle: () => void; onKeep: (r: HostRoomRow) => void }) {
  const navigate = useNavigate();
  const box = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: Event) => {
      const outside = e instanceof KeyboardEvent ? e.key === "Escape" : !box.current?.contains(e.target as Node);
      if (outside) onToggle();
    };
    document.addEventListener("pointerdown", away);
    document.addEventListener("keydown", away);
    return () => {
      document.removeEventListener("pointerdown", away);
      document.removeEventListener("keydown", away);
    };
  }, [open, onToggle]);
  return (
    <span className="sp-more" ref={box}>
      <button type="button" className={open ? "sp-go on" : "sp-go"} aria-label={`${room.title}, menu`} aria-expanded={open} onClick={onToggle}>
        <Icon name="more" />
      </button>
      {open ? (
        <span className="sp-menu" role="menu">
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              onToggle();
              onKeep(room);
            }}
          >
            <Icon name="pin" /> {room.kept ? "Take off the top" : "Keep at the top"}
          </button>
          <button type="button" role="menuitem" onClick={() => navigate(`/rooms/${encodeURIComponent(room.webinarId)}`)}>
            <Icon name="ext" /> Open the room's page
          </button>
        </span>
      ) : null}
    </span>
  );
}

/** A room that is not open: one line, with what is new or that a circle is live. */
export function ShutRoom({ room, menu = false, onMenu, onKeep }: { room: HostRoomRow; menu?: boolean; onMenu?: () => void; onKeep?: (r: HostRoomRow) => void }) {
  const navigate = useNavigate();
  const fresh = roomNew(room);
  return (
    <div className={menu ? "sp-room shut menu" : "sp-room shut"}>
      <button type="button" className="sp-shut" onClick={() => navigate(roomHome(room))}>
        <span className="sp-name">
          <Poster url={room.posterUrl} />
          <span>
            <b>{room.title}</b>
            <small>{kindLine(room)}</small>
          </span>
          {roomLive(room) ? <span className="ch-live">Live</span> : fresh ? <i className="ch-new">{fresh}</i> : null}
        </span>
      </button>
      {onMenu && onKeep ? <RoomMenu room={room} open={menu} onToggle={onMenu} onKeep={onKeep} /> : null}
    </div>
  );
}

/** The room that is open: one card, with its places nested on a line from its picture. */
function OpenRoom({
  room,
  place,
  menu,
  onMenu,
  onKeep,
  onCircle,
}: {
  room: HostRoomRow;
  place: Place;
  menu: boolean;
  onMenu: () => void;
  onKeep: (r: HostRoomRow) => void;
  onCircle: (webinarId: string) => void;
}) {
  const navigate = useNavigate();
  return (
    <div className="sp-room open">
      <p className="sp-name">
        <Poster url={room.posterUrl} />
        <span>
          <b>{room.title}</b>
          <small>{kindLine(room)}</small>
        </span>
        <RoomMenu room={room} open={menu} onToggle={onMenu} onKeep={onKeep} />
      </p>
      <div className="sp-tree">
        {room.questionsWaiting !== null ? (
          <button type="button" className={place.kind === "questions" ? "ch on" : "ch"} onClick={() => navigate(questionsPath(room.webinarId))}>
            <Icon name="chat" /> Questions
            {room.questionsWaiting ? <i className="ch-new">{room.questionsWaiting}</i> : null}
          </button>
        ) : null}
        {room.everyoneChat ? (
          <button type="button" className={place.kind === "everyone" ? "ch on" : "ch"} onClick={() => navigate(everyonePath(room.webinarId))}>
            <Icon name="users" /> Everyone
            {room.everyoneNew ? <i className="ch-new">{room.everyoneNew}</i> : null}
          </button>
        ) : null}
        <button type="button" className={place.kind === "announce" ? "ch on" : "ch"} onClick={() => navigate(announcementsPath(room.webinarId))}>
          <Icon name="bell" /> Announcements
        </button>
        <button type="button" className={place.kind === "files" ? "ch on" : "ch"} onClick={() => navigate(filesPath(room.webinarId))}>
          <Icon name="fold" /> Files
          <span className="ch-c">{room.filesCount}</span>
        </button>
        <p className="sp-sub">Circles</p>
        {room.circles.map((c) => (
          <button key={c.circleId} type="button" className={place.kind === "circle" && place.circleId === c.circleId ? "ch on" : "ch"} onClick={() => navigate(circlePath(c.circleId))}>
            <Icon name="target" /> {c.name}
            {c.live ? <span className="ch-live">Live</span> : c.newCount ? <i className="ch-new">{c.newCount}</i> : <span className="ch-c">{c.members}</span>}
          </button>
        ))}
        <button type="button" className="ch add" onClick={() => onCircle(room.webinarId)}>
          <Icon name="plus" /> Start a circle
        </button>
      </div>
    </div>
  );
}

/** The Rooms tab's list: kept rooms, then the ones active this week, then the way to every room. */
export function RoomSections({
  kept,
  active,
  total,
  open,
  place,
  onAll,
  onKeep,
  onCircle,
}: {
  kept: HostRoomRow[];
  active: HostRoomRow[];
  total: number;
  open: HostRoomRow | null;
  place: Place;
  onAll: () => void;
  onKeep: (r: HostRoomRow) => void;
  onCircle: (webinarId: string) => void;
}) {
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const toggle = (id: string) => () => setMenuFor((now) => (now === id ? null : id));
  const row = (r: HostRoomRow) =>
    r.webinarId === open?.webinarId ? (
      <OpenRoom key={r.webinarId} room={r} place={place} menu={menuFor === r.webinarId} onMenu={toggle(r.webinarId)} onKeep={onKeep} onCircle={onCircle} />
    ) : (
      <ShutRoom key={r.webinarId} room={r} menu={menuFor === r.webinarId} onMenu={toggle(r.webinarId)} onKeep={onKeep} />
    );
  // Why first: a room opened from All rooms may be neither kept nor active, and its places still show.
  const stray = open && ![...kept, ...active].some((r) => r.webinarId === open.webinarId) ? open : null;
  return (
    <div className="rail-s">
      {stray ? row(stray) : null}
      {kept.length ? (
        <>
          <p className="rail-sec">
            <Icon name="pin" /> Kept at the top
          </p>
          {kept.map(row)}
        </>
      ) : null}
      {active.length ? (
        <>
          <p className="rail-sec">Active this week</p>
          {active.map(row)}
        </>
      ) : null}
      <button type="button" className="rail-all" onClick={onAll}>
        <Icon name="grid" />
        <span>All rooms</span>
        <b>{total}</b>
        <Icon name="chev" />
      </button>
    </div>
  );
}

/** Every room, for when there are many: its own find box, the kinds with their counts, and the order. */
export function AllRooms({ rooms, onBack }: { rooms: HostRoomRow[]; onBack: () => void }) {
  const navigate = useNavigate();
  const [find, setFind] = useState("");
  const [kind, setKind] = useState<TalkRoomKind | "all">("all");
  const [order, setOrder] = useState<"recent" | "az">("recent");
  const view = allRoomsView(rooms, find, kind, order);
  const named = view.kinds.reduce((n, k) => n + k.count, 0);
  return (
    <div className="rail-s ar">
      <div className="ar-h">
        <button type="button" className="ar-back" aria-label="Back" onClick={onBack}>
          <Icon name="left" />
        </button>
        <b>All rooms</b>
        <span>{rooms.length}</span>
      </div>
      <label className="rail-q">
        <Icon name="search" />
        <input aria-label="Find a room" placeholder="Find a room" value={find} onChange={(e) => setFind(e.target.value)} />
      </label>
      <div className="ar-chips">
        <button type="button" className={kind === "all" ? "on" : undefined} onClick={() => setKind("all")}>
          All {named}
        </button>
        {view.kinds.map((k) => (
          <button key={k.kind} type="button" className={kind === k.kind ? "on" : undefined} onClick={() => setKind(k.kind)}>
            {k.kind} {k.count}
          </button>
        ))}
      </div>
      <div className="ar-order">
        <button type="button" className={order === "recent" ? "on" : undefined} onClick={() => setOrder("recent")}>
          Latest talk
        </button>
        <button type="button" className={order === "az" ? "on" : undefined} onClick={() => setOrder("az")}>
          A to Z
        </button>
      </div>
      <div className="ar-list">
        {view.shown.map((r) => (
          <button
            key={r.webinarId}
            type="button"
            className="ar-row"
            onClick={() => {
              onBack();
              navigate(roomHome(r));
            }}
          >
            <Poster url={r.posterUrl} />
            <span className="ar-t">
              <b>{r.title}</b>
              <small>{kindLine(r)}</small>
            </span>
            <span className="ar-r">
              {r.kept ? <Icon name="pin" /> : null}
              {roomNew(r) ? <i className="ch-new">{roomNew(r)}</i> : <small>{lastTalkWords(r)}</small>}
            </span>
          </button>
        ))}
        {view.shown.length === 0 ? <p className="empty-note">No room has that name.</p> : null}
      </div>
    </div>
  );
}
